import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import { Button, List, Popover, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import PopoverMenuItem from "@entrilia/shared/components/PopoverMenuItem";
import StatusChip from "@entrilia/shared/components/StatusChip";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import biApi from "../../../../../api/biApi";
import type {
  SmartViewArtifact,
  SmartViewArtifactVersionSummary,
} from "../../../../../api/types/smartViewArtifactsTypes";

const publishVersion = withErrorHandling(biApi.smartViewArtifacts.publish);
const restoreVersion = withErrorHandling(biApi.smartViewArtifacts.restore);

type VersionAction = "publish" | "restore";

interface VersionListItemProps {
  version: SmartViewArtifactVersionSummary;
  isViewed: boolean;
  isLatest: boolean;
  isPublished: boolean;
  onSelect: (version: number) => void;
}

const VersionListItem = ({ version, isViewed, isLatest, isPublished, onSelect }: VersionListItemProps) => {
  const details = [convertISODateTime(version.createdAt), version.createdBy].filter(Boolean).join(" · ");
  return (
    <PopoverMenuItem selected={isViewed} onClick={() => onSelect(version.version)}>
      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography>Version {version.version}</Typography>
          {isLatest && <StatusChip label="Latest" neutral height="smaller" color={(t) => t.palette.text.secondary} />}
          {isPublished && <StatusChip label="Published" height="smaller" color={(t) => t.palette.success.main} />}
        </Stack>
        <Typography variant="caption" color="textSecondary" noWrap>
          {details}
        </Typography>
        {version.prompt && (
          <Typography variant="caption" color="textSecondary" noWrap>
            {version.prompt}
          </Typography>
        )}
      </Stack>
    </PopoverMenuItem>
  );
};

interface VersionListProps {
  versions: SmartViewArtifactVersionSummary[] | undefined;
  error: ApiError | undefined;
  viewedVersion: number;
  latestVersion: number;
  publishedVersion?: number;
  onSelect: (version: number) => void;
}

const VersionList = ({
  versions,
  error,
  viewedVersion,
  latestVersion,
  publishedVersion,
  onSelect,
}: VersionListProps) => {
  if (error) {
    logError(error, "[VersionMenu] listVersions");
    return (
      <Typography color="error" sx={{ p: 2 }}>
        The versions could not be loaded.
      </Typography>
    );
  }
  if (!versions) {
    return <InlineLoader sx={{ py: 2 }} />;
  }
  return (
    <List>
      {versions.map((version) => (
        <VersionListItem
          key={version.version}
          version={version}
          isViewed={version.version === viewedVersion}
          isLatest={version.version === latestVersion}
          isPublished={version.version === publishedVersion}
          onSelect={onSelect}
        />
      ))}
    </List>
  );
};

interface Props {
  artifactId: string;
  versions: SmartViewArtifactVersionSummary[] | undefined;
  versionsError: ApiError | undefined;
  viewedVersion: number;
  latestVersion: number;
  publishedVersion?: number;
  actionsDisabled?: boolean;
  onSelect: (version: number) => void;
  onPublished: (artifact: SmartViewArtifact) => void;
  onRestored: () => Promise<void>;
}

const VersionMenu = ({
  artifactId,
  versions,
  versionsError,
  viewedVersion,
  latestVersion,
  publishedVersion,
  actionsDisabled,
  onSelect,
  onPublished,
  onRestored,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [runningAction, setRunningAction] = useState<VersionAction>();

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (version: number) => {
    setAnchorEl(null);
    onSelect(version);
  };

  const handlePublishClick = async () => {
    setRunningAction("publish");
    const [artifact, error] = await publishVersion(artifactId, viewedVersion);
    setRunningAction(undefined);
    if (error) {
      logError(error, "[VersionMenu] publish");
      sendNotificationError(error.message);
      return;
    }
    onPublished(artifact);
  };

  const handleRestoreClick = async () => {
    setRunningAction("restore");
    const [restored, error] = await restoreVersion(artifactId, viewedVersion);
    if (error) {
      setRunningAction(undefined);
      logError(error, "[VersionMenu] restore");
      sendNotificationError(error.message);
      return;
    }
    await onRestored();
    setRunningAction(undefined);
    sendNotification(`Version ${viewedVersion} was restored as version ${restored.version}.`);
  };

  const isBusy = runningAction !== undefined;

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<HistoryRoundedIcon />}
        endIcon={<ExpandMoreRoundedIcon />}
        disabled={isBusy}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        Version {viewedVersion}
      </Button>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 480 } } }}
      >
        <VersionList
          versions={versions}
          error={versionsError}
          viewedVersion={viewedVersion}
          latestVersion={latestVersion}
          publishedVersion={publishedVersion}
          onSelect={handleSelect}
        />
      </Popover>
      {viewedVersion !== latestVersion && (
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<RestoreRoundedIcon />}
          loading={runningAction === "restore"}
          disabled={isBusy || actionsDisabled}
          onClick={handleRestoreClick}
        >
          Restore this version
        </Button>
      )}
      <Button
        variant="contained"
        loading={runningAction === "publish"}
        disabled={isBusy || actionsDisabled || viewedVersion === publishedVersion}
        onClick={handlePublishClick}
      >
        Publish
      </Button>
    </>
  );
};

export default VersionMenu;
